import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { onSetUserToken } from "../../../../redux/slices/user.slice";
import { useAppDispatch } from "../../../../redux/store";
import {
  useLoginMutation,
  useRenvoyerCodeConnexionMutation,
  useVerifierCodeConnexionMutation,
} from "../../../../utils/api/auth/auth.api";
import { AuthFormData } from "../../../../utils/api/auth/auth.type";
import { getRoutePath } from "../../../routes/routes";

export function validatePassword(Yup, message) {
  return Yup.string()
    .required(message)
    .min(8, "Mot de passe doit être d'au moins 8 caractères");
}

function UseLoginForm() {
  const [login, { error, data, isLoading }] = useLoginMutation();
  const [verifier, { isLoading: verification }] =
    useVerifierCodeConnexionMutation();
  const [renvoyer] = useRenvoyerCodeConnexionMutation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  // Seconde etape : le mot de passe est bon, reste le code recu par email.
  const [etape, setEtape] = useState<"identifiants" | "code">("identifiants");
  const [emailMasque, setEmailMasque] = useState("");
  const [emailSaisi, setEmailSaisi] = useState("");
  const [code, setCode] = useState("");
  const [erreurCode, setErreurCode] = useState("");
  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        email: yup
          .string()
          .email()
          .required("L'email est requis")
          .label("Email"),
        password: yup.string().required("Le mot de passe est requis."),
      }),
    []
  );

  const { register, handleSubmit, formState, setError, clearErrors } =
    useForm<AuthFormData>({
      resolver: yupResolver(validationSchema),
    });

  useEffect(() => {
    if (error) {
      const err = error as any;
      console.log(err)
      const { message = "Une erreur inconnue s'est produite" } = err.data || {};
      setError("email", { message: message });
    }
  }, [error]);

  // Un seul chemin d'arrivee, que le code soit exige ou non.
  const entrer = useCallback(
    (resultat: any) => {
      dispatch(
        onSetUserToken({ token: resultat.token, user: resultat.data })
      );
      setTimeout(() => {
        if (resultat.data.user_type === "admin") {
          navigate(getRoutePath("DashboardAdmin"));
        } else if (resultat.data.is_double_type) {
          navigate(getRoutePath("MerchantDashboard"));
        } else {
          navigate(getRoutePath("ListeProjets"));
        }
      }, 400);
    },
    [dispatch, navigate]
  );

  useEffect(() => {
    if (!data) return;
    if ((data as any).otp_requis) {
      setEmailMasque((data as any).email || "");
      setEtape("code");
      return;
    }
    if (data.token) entrer(data);
  }, [data, entrer]);

  const soumettre = handleSubmit((valeurs: any) => {
    setEmailSaisi(valeurs.email);
    setErreurCode("");
    return login(valeurs);
  });

  const validerCode = async () => {
    setErreurCode("");
    if (!code.trim()) return setErreurCode("Saisissez le code reçu par email.");
    const res: any = await verifier({ email: emailSaisi, code: code.trim() });
    if (res?.error) {
      return setErreurCode(res.error?.data?.message || "Code incorrect.");
    }
    entrer(res.data);
  };

  return {
    register,
    errors: formState.errors,
    onSubmit: soumettre,
    clearErrors,
    isLoading,
    // Seconde etape
    etape,
    emailMasque,
    code,
    setCode,
    erreurCode,
    validerCode,
    verification,
    renvoyerCode: () => renvoyer(emailSaisi),
    revenirAuxIdentifiants: () => {
      setEtape("identifiants");
      setCode("");
      setErreurCode("");
    },
  };
}

export default UseLoginForm;
